import { Table, DatePicker, Space, Button, type TableColumnsType, type TableProps } from 'antd';
import { useState, useEffect, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { ExportExcelButton, ColumnManagerDropdown } from '../../common';
import { useColumnManager } from '../../../hooks';
import * as XLSX from 'xlsx';

const PaginatedTableBalanza = ({ csvName, 
  getNroCliente, 
  getTotalTickets, 
  getKgAlgodonTotales, 
  getTotalDesmotado,
  getTotalNoDesmotado,
  getBarchartDataDesmotado,
  getBarchartLabels,
  getBarchartDataNoDesmotado,
  getIsLoading,
  getFullTableData
 }: any) => {

  // Fetch Data
  const [fullTableData, setFullTableData] = useState([]);
  const [filteredTableData, setFilteredTableData] = useState(fullTableData)
  const [loading, setLoading] = useState(false)

  const {user, getAccessTokenSilently} = useAuth0();

  const id_empresa = user?.id_empresa
  const id_cliente = user?.id_cliente
  const tipo_usuario = user?.tipo_usuario 

  const variables_api = `${tipo_usuario}/${id_empresa}/${id_cliente}`
  
  // Para filtrar
  const desired_output = (data: any, column: any) => {
      const unique_values = data
          .map((item: any) => item[column])
          .filter(
              (value: any, index: any, current_value: any) => current_value.indexOf(value) === index
          );
      return unique_values;
  };

  // Para cuando se filtre la tabla desde la tabla.
  // En lugar de modificar la tabla completa, usamos otra variable.
  const onChange: TableProps<DataType>['onChange'] = (pagination, filters, sorter, extra: any) => {
      setFilteredTableData(extra.currentDataSource);
      getNroCliente(filters.cliente);
    };

  //var inicio = getYearMonthDay(new Date(fechaInicio))
  //var fin = getYearMonthDay(new Date(fechaFin))

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const apiEndpoint = `${process.env.REACT_APP_REPORTES_URL}/balanza_ingreso/${variables_api}`;
        const token = await getAccessTokenSilently();
        const response = await fetch(apiEndpoint, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        const respuesta = await response.json();
        setFullTableData(Array.isArray(respuesta?.data) ? respuesta.data : []);
        getTotalTickets(respuesta?.tickets_totales || 0);
        getKgAlgodonTotales(respuesta?.kg_algodon_bruto_totales || 0);
        getTotalDesmotado(respuesta?.total_desmotado || 0);
        getTotalNoDesmotado(respuesta?.total_no_desmotado || 0);
        getBarchartDataDesmotado(Array.isArray(respuesta?.barchart_data_desmotado) ? respuesta.barchart_data_desmotado : []);
        getBarchartDataNoDesmotado(Array.isArray(respuesta?.barchart_data_no_desmotado) ? respuesta.barchart_data_no_desmotado : []);
        getBarchartLabels(Array.isArray(respuesta?.barchart_labels) ? respuesta.barchart_labels : []);
        getFullTableData(Array.isArray(respuesta?.data) ? respuesta.data : []);
        setLoading(false);
        getIsLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        setLoading(false);
        getIsLoading(false);
      }
    };
    fetchData();
  }, [getAccessTokenSilently]); 

  // Clientes únicos para el filtro.
  const clientesUnicos = desired_output(fullTableData, 'cliente')
                              .map((cliente: any) => ({text: cliente, value: cliente}));

  const empresaUnicos = desired_output(fullTableData, 'empresa')
                              .map((empresa: any) => ({text: empresa, value: empresa}))

  const establecimientoUnicos = desired_output(fullTableData, 'establecimiento')
                              .map((establecimiento: any) => ({text: establecimiento, value: establecimiento}))
  
  const cosechaUnicos = desired_output(fullTableData, 'cosecha')
                              .map((cosecha: any) => ({text: cosecha, value: cosecha}))                           

  const productorUnicos = desired_output(fullTableData, 'productor')
                              .map((productor: any) => ({text: productor, value: productor}))

  const presentacionUnicos = desired_output(fullTableData, 'presentacion')
                              .map((presentacion: any) => ({text: presentacion, value: presentacion}))

  const productoUnicos = desired_output(fullTableData, 'producto')
                              .map((producto: any) => ({text: producto, value: producto}))

  const transportistaUnicos = desired_output(fullTableData, 'transportista')
                              .map((transportista: any) => ({text: transportista, value: transportista}))

  const choferUnicos = desired_output(fullTableData, 'chofer')
                              .map((chofer: any) => ({text: chofer, value: chofer}))

  const compra_servicioUnicos = desired_output(fullTableData, 'compra_servicio')
                              .map((compra_servicio: any) => ({text: compra_servicio, value: compra_servicio}))
  
  const desmotadoUnicos = desired_output(fullTableData, 'desmotado')
                              .map((desmotado: any) => ({text: desmotado, value: desmotado}))

  // Reusable date filter function
  const createDateFilter = (dateField: string) => {
    return {
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
        <div style={{ padding: 8 }}>
          <DatePicker.RangePicker
            value={selectedKeys[0]}
            onChange={(dates) => {
              setSelectedKeys(dates ? [dates] : []);
            }}
            style={{ marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => confirm()}
              size="small"
              style={{ width: 90 }}
            >
              Filter
            </Button>
            <Button
              onClick={() => {
                clearFilters?.();
                confirm();
              }}
              size="small"
              style={{ width: 90 }}
            >
              Reset
            </Button>
          </Space>
        </div>
      ),
      onFilter: (value: any, record: any) => {
        const date = new Date(record[dateField]);
        return date >= value[0].toDate() && date <= value[1].toDate();
      },
    };
  };

  // Table
  type DataType = {
    nro_de_ticket : number
    cliente : string
    empresa : string
    establecimiento : string
    nro_lote : number
    cosecha : string
    presentacion : string
    productor : string
    producto : string
    nro_remito : number
    dtv : string
    cantidad_rollos : number
    transportista : string
    chofer : string
    camion : string
    ingreso_salida : string
    compra_servicio : string
    primer_pasada_kg : number 
    primer_pasada_horario : Date
    segunda_pasada_kg : number 
    segunda_pasada_horario : Date
    duracion : number
    peso : number
    fecha_ticket : Date
    ciclo : string
    desmotado: string
    }
    
  const allColumns: TableColumnsType<DataType> = [
    {
      title: 'Desmotadora',
      dataIndex: 'empresa',
      key: 'empresa',
      width: 120,
      ellipsis: true,

      filters: empresaUnicos,
      onFilter: (value, record) => record.empresa == value
    },
    {
      title: 'Cliente',
      dataIndex: 'cliente',
      key: 'cliente',
      width: 150,
      ellipsis: true,

      filters: clientesUnicos,
      onFilter: (value, record) => record.cliente == value
    },
    {
      title: 'Productor',
      dataIndex: 'productor',
      key: 'productor',
      width: 150,
      ellipsis: true,

      filters: productorUnicos,
      onFilter: (value, record) => record.productor == value
    },
    {
      title: 'N° Ticket',
      dataIndex: 'nro_de_ticket',
      key: 'nro_de_ticket',
      width: 100,

      defaultSortOrder: 'descend',
      sorter: (a, b) => a.nro_de_ticket - b.nro_de_ticket,
    },
    {
      title: 'Estab.',
      dataIndex: 'establecimiento',
      key: 'establecimiento',
      width: 100,
      ellipsis: true,

      filters: establecimientoUnicos,
      onFilter: (value, record) => record.establecimiento == value
    },
    {
      title: 'Nro. Lote',
      dataIndex: 'nro_lote',
      key: 'nro_lote',
      width: 100,

      defaultSortOrder: 'descend',
      sorter: (a, b) => a.nro_lote - b.nro_lote,
    },
    {
      title: 'Cosecha',
      dataIndex: 'cosecha',
      key: 'cosecha',
      width: 100,
      ellipsis: true,

      filters: cosechaUnicos,
      onFilter: (value, record) => record.cosecha == value        
    },
    {
      title: 'Present.',
      dataIndex: 'presentacion',
      key: 'presentacion',
      width: 100,
      ellipsis: true,

      filters: presentacionUnicos,
      onFilter: (value, record) => record.presentacion == value
    },
    {
      title: 'Producto',
      dataIndex: 'producto',
      key: 'producto',
      width: 120,
      ellipsis: true,

      filters: productoUnicos,
      onFilter: (value, record) => record.producto == value
    },
    {
      title: 'Nro. Remito',
      dataIndex: 'nro_remito',
      key: 'nro_remito',
      width: 110,

      defaultSortOrder: 'descend',
      sorter: (a, b) => a.nro_remito - b.nro_remito,
    },
    {
      title: 'DTV',
      dataIndex: 'dtv',
      key: 'dtv',
      width: 110,
      ellipsis: true,
    },
    {
      title: 'Cant. Rollos',
      dataIndex: 'cantidad_rollos',
      key: 'cantidad_rollos',
      width: 110,

      defaultSortOrder: 'descend',
      sorter: (a, b) => a.cantidad_rollos - b.cantidad_rollos,
    },
    {
      title: 'Transportista',
      dataIndex: 'transportista',
      key: 'transportista',
      width: 150,
      ellipsis: true,

      filters: transportistaUnicos,
      onFilter: (value, record) => record.transportista == value
    },
    {
      title: 'Chofer',
      dataIndex: 'chofer',
      key: 'chofer',
      width: 130,
      ellipsis: true,

      filters: choferUnicos,
      onFilter: (value, record) => record.chofer == value
    },
    {
      title: 'Camion',
      dataIndex: 'camion',
      key: 'camion',
      width: 120,
      ellipsis: true,

    },
    {
      title: 'Comp./Serv.',
      dataIndex: 'compra_servicio',
      key: 'compra_servicio',
      width: 110,
      ellipsis: true,

      filters: compra_servicioUnicos,
      onFilter: (value, record) => record.compra_servicio == value
    },
    {
      title: '1ra Pas. Kg',
      dataIndex: 'primer_pasada_kg',
      key: 'primer_pasada_kg',
      width: 110,
      align: 'right',

      defaultSortOrder: 'descend',
      sorter: (a, b) => a.primer_pasada_kg - b.primer_pasada_kg,
    },
    {
      title: '1ra Pas. Hora',
      dataIndex: 'primer_pasada_horario',
      key: 'primer_pasada_horario',
      width: 130,

      ...createDateFilter('primer_pasada_horario')
    },
    {
      title: '2da Pas. Kg',
      dataIndex: 'segunda_pasada_kg',
      key: 'segunda_pasada_kg',
      width: 110,
      align: 'right',

      defaultSortOrder: 'descend',
      sorter: (a, b) => a.segunda_pasada_kg - b.segunda_pasada_kg
    },
    {
      title: '2da Pas. Hora',
      dataIndex: 'segunda_pasada_horario',
      key: 'segunda_pasada_horario',
      width: 130,

      ...createDateFilter('segunda_pasada_horario')
    },
    {
      title: 'Duración',
      dataIndex: 'duracion',
      key: 'duracion',
      width: 100,
      align: 'right',

      defaultSortOrder: 'descend',
      sorter: (a, b) => a.duracion - b.duracion,
    },
    {
      title: 'Peso',
      dataIndex: 'peso',
      key: 'peso',
      width: 100,
      align: 'right',

      defaultSortOrder: 'descend',
      sorter: (a, b) => a.peso - b.peso,
    },
    {
      title: 'Fecha Ticket',
      dataIndex: 'fecha_ticket',
      key: 'fecha_ticket',
      width: 130,

      ...createDateFilter('fecha_ticket')
    },
    {
      title: 'Ciclo',
      dataIndex: 'ciclo',
      key: 'ciclo',
      width: 100,
      ellipsis: true,

    },
    {
      title: 'Desmotado',
      dataIndex: 'desmotado',
      key: 'desmotado',
      width: 110,
      ellipsis: true,

      filters: desmotadoUnicos,
      onFilter: (value, record) => record.desmotado == value
    },
  ]

  // Lista de todas las columnas disponibles (keys)
  const allColumnKeys = allColumns.map(col => col.key as string).filter(Boolean);

    // Nombres amigables para las columnas
    const columnNames: Record<string, string> = {
        'empresa': 'Desmotadora',
        'cliente': 'Cliente',
        'productor': 'Productor',
        'nro_de_ticket': 'N° Ticket',
        'establecimiento': 'Estab.',
        'nro_lote': 'Nro. Lote',
        'cosecha': 'Cosecha',
        'presentacion': 'Present.',
        'producto': 'Producto',
        'nro_remito': 'Nro. Remito',
        'dtv': 'DTV',
        'cantidad_rollos': 'Cant. Rollos',
        'transportista': 'Transportista',
        'chofer': 'Chofer',
        'camion': 'Camion',
        'compra_servicio': 'Comp./Serv.',
        'primer_pasada_kg': '1ra Pas. Kg',
        'primer_pasada_horario': '1ra Pas. Hora',
        'segunda_pasada_kg': '2da Pas. Kg',
        'segunda_pasada_horario': '2da Pas. Hora',
        'duracion': 'Duración',
        'peso': 'Peso',
        'fecha_ticket': 'Fecha Ticket',
        'ciclo': 'Ciclo',
        'desmotado': 'Desmotado'
    };

  // Visibilidad + orden de columnas (persistido) — reemplaza el manejo manual de antes.
  const columnManager = useColumnManager({
      storageKey: 'balanza_ingresos',
      allKeys: allColumnKeys,
  });

  // Columnas visibles, en el orden elegido por el usuario en el selector de columnas
  const filteredColumns = useMemo(() => {
      const columnsByKey = new Map(allColumns.map(col => [col.key as string, col]));
      return columnManager.orderedVisibleKeys
          .map(key => columnsByKey.get(key))
          .filter((col): col is (typeof allColumns)[number] => Boolean(col));
  }, [columnManager.orderedVisibleKeys, allColumns]);

  const columns: TableColumnsType<DataType> = filteredColumns;

    const data: DataType[] = fullTableData.map((fila: any) => {
        return {
          nro_de_ticket : fila.nro_de_ticket,
          cliente : fila.cliente,
          empresa : fila.empresa,
          establecimiento : fila.establecimiento,
          nro_lote : fila.nro_lote,
          cosecha : fila.cosecha,
          presentacion : fila.presentacion,
          productor : fila.productor,
          producto : fila.producto,
          nro_remito : fila.nro_remito,
          dtv : fila.dtv || '',
          cantidad_rollos : fila.cantidad_rollos,
          transportista : fila.transportista,
          chofer : fila.chofer,
          camion : fila.camion,
          ingreso_salida : fila.ingreso_salida,
          compra_servicio : fila.compra_servicio,
          primer_pasada_kg : fila. primer_pasada_kg,
          primer_pasada_horario : fila.primer_pasada_horario,
          segunda_pasada_kg : fila.segunda_pasada_kg,
          segunda_pasada_horario : fila.segunda_pasada_horario,
          duracion : fila.duracion,
          peso : fila.peso,
          fecha_ticket : fila.fecha_ticket,
          ciclo : fila.ciclo,
          desmotado : fila.desmotado
        }
    });

  // Función para exportar a Excel
  const handleExportExcel = () => {
      try {
          const sourceData: any[] = filteredTableData.length > 0 ? filteredTableData : data;
          const exportData = sourceData.map((row: any) => {
              const exportRow: Record<string, any> = {};
              columnManager.orderedVisibleKeys.forEach(key => {
                  exportRow[columnNames[key] || key] = row[key];
              });
              return exportRow;
          });
          const worksheet = XLSX.utils.json_to_sheet(exportData);
          const workbook = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(workbook, worksheet, 'Balanza Ingresos');
          XLSX.writeFile(workbook, csvName || `balanza_ingresos_${new Date().toISOString().split('T')[0]}.xlsx`);
      } catch (error) {
          console.error('Error al exportar Excel:', error);
      }
  };

    return (
      <div>
        <div style={{ 
            marginBottom: '16px', 
            display: 'flex', 
            gap: '12px', 
            justifyContent: 'flex-end',
            alignItems: 'center',
            flexWrap: 'wrap'
        }}>
            <ColumnManagerDropdown
                manager={columnManager}
                labels={columnNames}
                buttonSize="middle"
            />
            <ExportExcelButton
                onClick={handleExportExcel}
                size="middle"
                disabled={fullTableData.length === 0}
            />
        </div>
        <Table 
            columns={columns} 
            dataSource={data}
            bordered
            size="middle"
            scroll={{ x: 'max-content', y: 'max(150px, calc(100vh - 400px))' }}
            onChange={onChange}
            loading={loading}
            pagination={{
                pageSize: 50,
                showSizeChanger: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} de ${total}`,
                pageSizeOptions: ['10', '25', '50', '100'],
                position: ['bottomRight']
            }}
        >
        </Table>
      </div>
    );
  };
  
  export default PaginatedTableBalanza;
